import { datadogRum } from '@datadog/browser-rum';
import { AxiosRequestConfig } from 'axios';
import { ComputedRef, computed, reactive, ref } from 'vue';
import AcordoExternoDto from '@/models/dto/AcordoExternoDto';
import DividaDto from '@/models/dto/DividaDto';
import OfertaSugeridaDto from '@/models/dto/OfertaSugeridaDto';
import PropostaDto from '@/models/dto/PropostaDto';
import PropostaNovoDto from '@/models/dto/PropostaNovoDto';
import SincronizacaoPropostasDto from '@/models/dto/SincronizacaoPropostasDto';
import WlSincronizacaoDto from '@/models/dto/WlSincronizacaoDto';
import { StatusProposta } from '@/models/enums';
import ApiErrorCode from '@/models/enums/ApiErrorCode';
import { OfertaSugeridaResource, PropostaNovoResource, PropostaResource } from '@/models/resources';
import AcordoExternoResource from '@/models/resources/AcordoExternoResource';
import ApiError from '@/models/resources/ApiError';
import { ApiResponse } from '@/models/resources/ApiResponse';
import SincronizacaoPropostasResource from '@/models/resources/SincronizacaoPropostasResource';
import { PropostasViewModel } from '@/models/viewModels/PropostasViewModel';
import usePropostasStore from '@/stores/propostas';
import ConvivenciaWlSevice from './ConvivenciaWlSevice';
import HttpService from './HttpService';
import sessionService from './SessionService';

interface EnvioProposta {
  dividaId: number;
  valorEntrada: number;
  quantidadeParcelas: number;
  valorParcela: number;
  dataPrimeiroBoleto: Date;
}

const NEXT_REQUEST_TIME = 1000;
const TIMEOUT = 10000;

class PropostaService extends HttpService {
  private timeoutCount: number;
  private timer: ReturnType<typeof setInterval> | null;
  private readonly syncControllers: Set<AbortController>;
  private readonly sincronizacoesPendentes = ref(0);
  private readonly sincronizacaoBemSucedidaPorApi = reactive<Record<string, boolean>>({});

  /** ViewModel reativo para as telas se plugarem. */
  public viewModel: ComputedRef<PropostasViewModel>;

  constructor() {
    super();

    this.timeoutCount = 0;
    this.timer = null;
    this.syncControllers = new Set();
    this.viewModel = computed(() => {
      const store = usePropostasStore();
      return {
        aguardandoPagamento: [
          ...PropostaService.listarPropostasPorStatus(new Set([StatusProposta.Aceita]), store.propostasPorCredor),
          ...PropostaService.listarAcordosExternosPorStatus(
            new Set([StatusProposta.Aceita]),
            store.acordosExternosPorCredor
          ),
        ],
        emAnalise: PropostaService.listarPropostasPorStatus(
          new Set([StatusProposta.EmAnalise, StatusProposta.PendenteDeRetorno]),
          store.propostasPorCredor
        ),
        quitadas: [
          ...PropostaService.listarPropostasPorStatus(new Set([StatusProposta.Quitada]), store.propostasPorCredor),
          ...PropostaService.listarAcordosExternosPorStatus(
            new Set([StatusProposta.Quitada]),
            store.acordosExternosPorCredor
          ),
        ],
        credoresComProposta: usePropostasStore().listarCredoresComProposta(),
        sincronizado:
          this.sincronizacoesPendentes.value === 0 &&
          (sessionService.token?.apis ?? []).every(api => this.sincronizacaoBemSucedidaPorApi[api] === true),
        sincronizacaoEmAndamento: this.sincronizacoesPendentes.value > 0,
      };
    });
  }

  async sincronizar(api: string) {
    this.sincronizacoesPendentes.value += 1;
    this.sincronizacaoBemSucedidaPorApi[api] = false;
    try {
      this.sincronizacaoBemSucedidaPorApi[api] = await this.executarSincronizacao(api);
    } finally {
      this.sincronizacoesPendentes.value -= 1;
    }
  }

  public registrarPropostasAtualizadas(api: string) {
    this.sincronizacaoBemSucedidaPorApi[api] = true;
  }

  private async executarSincronizacao(api: string): Promise<boolean> {
    const controller = new AbortController();
    this.syncControllers.add(controller);

    if (ConvivenciaWlSevice.isWhiteLabel(api)) {
      try {
        const whitelabelResp = await this.listarDividasWl(api, controller.signal);
        if (controller.signal.aborted) {
          return false;
        }
        await ConvivenciaWlSevice.filtrarNegociaveisEAtualizarStorePropostas(api, whitelabelResp);
      } finally {
        this.syncControllers.delete(controller);
      }
      return true;
    }
    try {
      datadogRum.addAction('sys:proposta_service:sincronizar', { api });
      const url = `/api/${api}/Consumidor/Propostas/Sincronizar`;
      const response = await this.post<ApiResponse<SincronizacaoPropostasDto>>(url, undefined, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        return false;
      }
      const sincronizacao = new SincronizacaoPropostasResource(api, response.data);
      const store = usePropostasStore();
      store.atualizarTodasPropostas(api, sincronizacao.propostas);
      store.atualizarTodosAcordosExternos(api, sincronizacao.acordosExternos);
    } finally {
      this.syncControllers.delete(controller);
    }
    return true;
  }

  public async obterProposta(
    api: string,
    idProposta: number,
    useCache = true
  ): Promise<PropostaNovoResource | undefined> {
    const propostasStore = usePropostasStore();
    if (useCache) {
      const propostaCache = propostasStore.obterPropostaPorId(api, idProposta);
      if (propostaCache) {
        return propostaCache;
      }
    }

    datadogRum.addAction('sys:proposta_service:obter_proposta', { api, idProposta });
    try {
      const response = await this.get<ApiResponse<PropostaNovoDto | undefined>>(
        `/api/${api}/Consumidor/Propostas/${idProposta}`
      );

      // WL retorna 200 com isValid = true,
      // logo é preciso tratar o caso de não encontrar a proposta
      if (!response.data) {
        return undefined;
      }
      const proposta = ConvivenciaWlSevice.isWhiteLabel(api)
        ? ConvivenciaWlSevice.fromPropostaToPropostaNovoResource(response.data)
        : new PropostaNovoResource(response.data);

      propostasStore.atualizarProposta(api, proposta);
      return proposta;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown) {
      if (error instanceof ApiError && error.is(ApiErrorCode.PROPOSTA_NAO_ENCONTRADA)) {
        return undefined;
      }
      throw error;
    }
  }

  /**
   * Buscar a proposta do MC ou a última proposta do WL associada à dívida.
   * @param api API do credor, com prefixo mc- para o MultiCredor e sem para wl.
   * @param id Id da Proposta para MultiCredor ou id da Dívda para WhiteLabel.
   * @param useCache Deve tentar buscar primeiro no cache?
   * @returns A proposta encontrada. Para WL retorna a última proposta associada à dívida.
   */
  public async obterUltimaProposta(
    api: string,
    id: number,
    useCache = true
  ): Promise<PropostaNovoResource | undefined> {
    if (ConvivenciaWlSevice.isWhiteLabel(api)) {
      const propostaWl = await this.obterUltimaPropostaWl(api, id);
      return propostaWl;
    }
    if (useCache) {
      const propostasStore = usePropostasStore();
      const propostas = propostasStore.listarPropostasPorCredor(api);
      const ultima = propostas.filter(p => p.divida.id === id).sort((a, b) => b.id - a.id)[0];
      return ultima;
    }
    const propostaMc = await this.obterProposta(api, id, false);
    return propostaMc;
  }

  public async obterAcordoExterno(
    api: string,
    idAcordoExterno: number,
    useCache = true
  ): Promise<AcordoExternoResource> {
    const propostasStore = usePropostasStore();
    if (useCache) {
      const acordoCache = propostasStore.obterAcordoExternoPorId(api, idAcordoExterno);
      if (acordoCache) {
        return acordoCache;
      }
    }

    datadogRum.addAction('sys:proposta_service:obter_acordo_externo', { api, idAcordoExterno });
    const response = await this.get<ApiResponse<AcordoExternoDto>>(
      `/api/${api}/Consumidor/Acordosexternos/${idAcordoExterno}`
    );
    propostasStore.atualizarAcordoExterno(api, response.data);
    return new AcordoExternoResource(api, response.data);
  }

  async enviarProposta(api: string, proposta: EnvioProposta, origem = '') {
    datadogRum.addAction('sys:proposta_service:enviar_proposta', { api, proposta, origem });
    const reqConfig: AxiosRequestConfig = origem ? { params: { origem } } : {};
    const response = await this.post<ApiResponse<PropostaDto>>(`/api/${api}/Consumidor/Proposta`, proposta, reqConfig);
    usePropostasStore().limpar(api);
    return new PropostaResource(response.data);
  }

  // eslint-disable-next-line class-methods-use-this
  possuiUnicoAcordo(apis: string[] | undefined) {
    const propostasStore = usePropostasStore();
    const propostas = propostasStore.listarPropostas();
    const propostasFiltradas = apis ? propostas.filter(p => apis.includes(p.divida.api)) : propostas;

    return propostasFiltradas.filter(p => p.possuiParcela).length === 1;
  }

  /**
   * Monitorar propostas aguardando confirmação no credor.
   * @param api Slug do credor.
   * @param propostaId ID da proposta quando vindo do MultiCredor.
   * @param dividaId ID da Dívida quando vindo do WL.
   * @param callback Chamado quando a proposta é encontrada em um status final.
   * @param timeoutCallback Chamado quando o timeout é atingido.
   * @returns void
   */
  public async monitorarPropostaPendenteRetorno(
    api: string,
    propostaId: number,
    dividaId: number,
    callback: (proposta: PropostaNovoResource) => void,
    timeoutCallback?: () => void
  ) {
    const proposta = ConvivenciaWlSevice.isWhiteLabel(api)
      ? await this.obterUltimaPropostaWl(api, dividaId)
      : await this.obterProposta(api, propostaId, false);
    if (!this.possuiTimer) {
      this.iniciarTimer();
    }

    if (proposta && proposta.status !== StatusProposta.PendenteDeRetorno) {
      callback(proposta);
      this.pararTimer();
      return;
    }

    if (timeoutCallback && this.timeoutCount > TIMEOUT) {
      this.pararTimer();
      timeoutCallback();
      return;
    }

    setTimeout(() => {
      this.monitorarPropostaPendenteRetorno(api, propostaId, dividaId, callback, timeoutCallback);
    }, NEXT_REQUEST_TIME);
  }

  async obterTermoAcordo(api: string, propostaId: number) {
    datadogRum.addAction('sys:proposta_service:obter_termo_acordo', { api, propostaId });
    const response = await this.get<ApiResponse<string>>(`/api/${api}/Consumidor/TermoAcordo`, {
      params: { propostaId },
      responseType: 'json',
    });

    // `atob` devolve uma string binária, um caractere por byte (0–255): `codePointAt` dá o próprio byte.
    const bufferBytes = Uint8Array.from(atob(response.data), c => c.codePointAt(0) ?? 0).buffer;
    return new Blob([bufferBytes], { type: 'application/pdf' });
  }

  async obterSugestaoProposta(api: string, hashOferta: string) {
    datadogRum.addAction('sys:proposta_service:obter_sugestao_proposta', { api, hashOferta });
    const url = `/api/${api}/Consumidor/SugestaoProposta/${hashOferta}`;
    const response = await this.get<ApiResponse<OfertaSugeridaDto>>(url);
    return new OfertaSugeridaResource(response.data);
  }

  async cancelarAcordo(api: string, dividaId: number) {
    datadogRum.addAction('sys:proposta_service:cancelar_acordo', { api, dividaId });
    const url = `/api/${api}/consumidor/dividas/${dividaId}/acordo`;
    await this.delete<void>(url);
    usePropostasStore().limpar(api);
  }

  get possuiTimer(): boolean {
    return this.timer !== null;
  }

  // eslint-disable-next-line class-methods-use-this
  public limparTodosCredores() {
    Object.keys(this.sincronizacaoBemSucedidaPorApi).forEach(api => delete this.sincronizacaoBemSucedidaPorApi[api]);
    usePropostasStore().limparTodosCredores();
  }

  public cancelarSincronizacoesEmAndamento() {
    this.syncControllers.forEach(controller => controller.abort());
    this.syncControllers.clear();
    this.pararTimer();
  }

  private static listarPropostasPorStatus(
    statuses: Set<StatusProposta>,
    propostasPorCredor: Record<string, PropostaNovoResource[]>
  ): PropostaNovoResource[] {
    return Object.values(propostasPorCredor)
      .flat()
      .filter(proposta => statuses.has(proposta.status));
  }

  private static listarAcordosExternosPorStatus(
    statuses: Set<StatusProposta>,
    acordosExternosPorCredor: Record<string, AcordoExternoResource[]>
  ): AcordoExternoResource[] {
    return Object.values(acordosExternosPorCredor)
      .flat()
      .filter(acordo => statuses.has(acordo.status));
  }

  private async obterUltimaPropostaWl(api: string, idDivida: number): Promise<PropostaNovoResource | undefined> {
    const dividaWl = await this.obterDividaWl(api, idDivida);
    if (dividaWl) {
      const proposta = ConvivenciaWlSevice.fromDividaToPropostaNovoResource(dividaWl);
      if (proposta) {
        usePropostasStore().atualizarProposta(api, proposta);
        return proposta;
      }
    }
    return undefined;
  }

  private async obterDividaWl(api: string, dividaId: number): Promise<DividaDto> {
    datadogRum.addAction('sys:proposta_service:obter_divida', { api, dividaId });
    const response = await this.get<ApiResponse<DividaDto>>(`/api/${api}/Consumidor/Dividas/${dividaId}`);
    return response.data;
  }

  private async listarDividasWl(api: string, signal?: AbortSignal): Promise<WlSincronizacaoDto> {
    datadogRum.addAction('sys:proposta_service:listar_dividas_wl', { api });
    const url = `/api/${api}/Consumidor/Dividas`;
    const response = signal
      ? await this.get<ApiResponse<WlSincronizacaoDto>>(url, { signal })
      : await this.get<ApiResponse<WlSincronizacaoDto>>(url);
    return response.data;
  }

  private iniciarTimer() {
    this.timeoutCount = 0;
    this.timer = setInterval(() => {
      this.timeoutCount = +1000;
    }, 1000);
  }

  private pararTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.timeoutCount = 0;
  }
}

const propostaService = new PropostaService();
export default propostaService;
